import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Ban, ChevronLeft, ChevronRight, Plus, Receipt, Search, X } from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { InvoiceStatusBadge, invoiceStatusLabel } from "../components/InvoiceStatusBadge";
import { Button, EmptyState, Modal, PageHeader, TableSkeleton, cx } from "../components/ui";
import { formatDate, formatMoney } from "../lib/format";
import { isAccountAdmin, isManagerOrAbove } from "../lib/people";
import { useAccountTimeZone } from "../lib/useAccountTimeZone";
import type { Invoice, InvoiceStatus } from "../api/types";

/**
 * The lifecycle statuses an invoice can be *stored* in. `overdue` is derived at
 * read time (FR-23.4) and never stored, so the API cannot filter by it -- an
 * overdue invoice is reported by its badge instead of a filter that would
 * always come back empty.
 */
const FILTERS: Array<"" | InvoiceStatus> = ["", "draft", "sent", "part_paid", "paid", "void"];

/**
 * The invoice register.
 *
 * One row per invoice, newest first. Search and status are the filters the API
 * supports; filter and page state live in the URL so a filtered view is
 * linkable and survives a refresh. Cursor pagination cannot produce "page 3 of
 * 9", so the control is previous/next -- see `apps/api/pagination.py`.
 *
 * Only the register is wired. Drafting, payments and credit notes are separate
 * work; nothing here fabricates them.
 */
export function InvoicesPage() {
  const timeZone = useAccountTimeZone();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();

  const search = params.get("search") ?? "";
  const status = params.get("status") ?? "";
  const cursor = params.get("cursor") ?? "";

  // The input is local so typing stays responsive; the URL updates on a debounce
  // so we are not firing a request per keystroke.
  const [draft, setDraft] = useState(search);
  const debounceTimer = useRef<number | undefined>(undefined);

  useEffect(() => setDraft(search), [search]);
  useEffect(() => () => window.clearTimeout(debounceTimer.current), []);

  function update(next: Record<string, string>) {
    setParams(
      (prev) => {
        const merged = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(next)) {
          if (value) merged.set(key, value);
          else merged.delete(key);
        }
        return merged;
      },
      { replace: true },
    );
  }

  function onDraftChange(value: string) {
    setDraft(value);
    window.clearTimeout(debounceTimer.current);
    debounceTimer.current = window.setTimeout(() => {
      update({ search: value, cursor: "" });
    }, 250);
  }

  const query = useQuery({
    queryKey: ["invoices", search, status, cursor],
    queryFn: ({ signal }) =>
      api.invoices(
        {
          search: search || undefined,
          status: status || undefined,
          cursor: cursor || undefined,
          page_size: 25,
        },
        signal,
      ),
    placeholderData: (prev) => prev,
  });

  const invoices = query.data?.results ?? [];
  const nextCursor = cursorFrom(query.data?.next);
  const filtering = Boolean(search || status);
  const canWrite = isManagerOrAbove(user?.role);

  const queryClient = useQueryClient();
  const [voidingInvoice, setVoidingInvoice] = useState<Invoice | null>(null);

  const voidMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.voidInvoice(id, reason),
    onSuccess: (updated) => {
      setVoidingInvoice(null);
      voidMutation.reset();
      void queryClient.invalidateQueries({ queryKey: ["invoices"] });
      void queryClient.invalidateQueries({ queryKey: ["invoice", updated.id] });
      void queryClient.invalidateQueries({ queryKey: ["billing"] });
    },
  });

  const clear = () => setParams(new URLSearchParams(), { replace: true });

  return (
    <>
      <PageHeader
        title="Invoices"
        subtitle={
          query.isSuccess
            ? `${invoices.length} shown${query.data?.next ? ", more available" : ""}`
            : "Send, void and print. The month-end register."
        }
        actions={
          canWrite ? (
            <Link
              to="/billing/invoices/new"
              className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
            >
              <Plus size={16} /> New invoice
            </Link>
          ) : undefined
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-56 flex-1">
          <Search
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            type="search"
            value={draft}
            onChange={(event) => onDraftChange(event.target.value)}
            placeholder="Search by invoice number"
            aria-label="Search by invoice number"
            className="w-full rounded-md border-0 bg-white py-2 pl-9 pr-3 text-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-900"
          />
        </div>

        <div className="flex flex-wrap gap-1">
          {FILTERS.map((value) => (
            <button
              key={value || "all"}
              onClick={() => update({ status: value, cursor: "" })}
              className={cx(
                "rounded-full px-3 py-1.5 text-xs font-medium transition",
                status === value
                  ? "bg-slate-900 text-white"
                  : "bg-white text-slate-600 ring-1 ring-inset ring-slate-300 hover:bg-slate-50",
              )}
            >
              {value ? invoiceStatusLabel(value) : "All"}
            </button>
          ))}
        </div>

        {filtering ? (
          <Button variant="ghost" onClick={clear}>
            <X size={14} /> Clear
          </Button>
        ) : null}
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
        {query.isPending ? (
          <TableSkeleton rows={8} />
        ) : query.isError ? (
          <ErrorState error={query.error} />
        ) : invoices.length === 0 ? (
          <EmptyState
            icon={<Receipt size={28} />}
            title={filtering ? "Nothing matches that" : "No invoices yet"}
            body={
              filtering
                ? "Try a different number, or clear the filters."
                : "Invoices you issue will appear here."
            }
            action={filtering ? <Button onClick={clear}>Clear filters</Button> : undefined}
          />
        ) : (
          <>
            {/* Desktop: a real table. */}
            <table className="hidden w-full sm:table">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2.5">Number</th>
                  <th className="px-4 py-2.5">Customer</th>
                  <th className="px-4 py-2.5">Issued</th>
                  <th className="px-4 py-2.5">Due</th>
                  <th className="px-4 py-2.5 text-right">Total</th>
                  <th className="px-4 py-2.5 text-right">Balance Due</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {invoices.map((invoice) => {
                  const voidable = canVoidInvoice(invoice, user?.role);
                  return (
                    <tr
                      key={invoice.id}
                      onClick={(event) => {
                        // The number is the real link; clicking it must not also
                        // trigger the row handler and push two history entries.
                        if ((event.target as HTMLElement).closest("a, button")) return;
                        navigate(`/billing/invoices/${invoice.id}`);
                      }}
                      className="cursor-pointer hover:bg-slate-50"
                    >
                      <td className="px-4 py-3 text-sm font-medium tabular-nums text-slate-900">
                        <Link
                          to={`/billing/invoices/${invoice.id}`}
                          aria-label={`Open invoice ${invoice.number || "draft"}`}
                          className="hover:underline"
                        >
                          {invoice.number || "Draft"}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-slate-900">
                          {invoice.contact_name || "—"}
                        </p>
                        {invoice.contact_phone ? (
                          <p className="mt-0.5 text-xs text-slate-500">{invoice.contact_phone}</p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-sm tabular-nums text-slate-600">
                        {formatDate(invoice.issue_date, timeZone)}
                      </td>
                      <td className="px-4 py-3 text-sm tabular-nums text-slate-600">
                        {formatDate(invoice.due_on, timeZone)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <p className="text-sm tabular-nums text-slate-900">
                          {formatMoney(invoice.total_amount)}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <p className="text-sm tabular-nums text-slate-900 font-medium">
                          {formatMoney(invoice.balance_due)}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <InvoiceStatusBadge status={invoice.display_status} />
                      </td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        {voidable ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setVoidingInvoice(invoice);
                            }}
                            className="inline-flex items-center gap-1 rounded px-2.5 py-1 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-300 hover:bg-slate-100 hover:text-slate-900 transition"
                            aria-label={`Void invoice ${invoice.number || "draft"}`}
                          >
                            <Ban size={13} className="text-slate-500" /> Void
                          </button>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Mobile: cards. A 6-column table on a phone is not a table. */}
            <ul className="divide-y divide-slate-100 sm:hidden">
              {invoices.map((invoice) => {
                const voidable = canVoidInvoice(invoice, user?.role);
                return (
                  <li key={invoice.id}>
                    <div className="block px-4 py-3 hover:bg-slate-50">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <Link
                            to={`/billing/invoices/${invoice.id}`}
                            className="truncate text-sm font-medium tabular-nums text-slate-900 hover:underline"
                          >
                            {invoice.number || "Draft"}
                          </Link>
                          <p className="truncate text-xs text-slate-500">
                            {invoice.contact_name || "—"}
                          </p>
                        </div>
                        <InvoiceStatusBadge status={invoice.display_status} />
                      </div>
                      <div className="mt-1.5 flex items-center justify-between gap-3 text-xs text-slate-500">
                        <span className="tabular-nums">
                          {formatDate(invoice.issue_date, timeZone)} →{" "}
                          {formatDate(invoice.due_on, timeZone)}
                        </span>
                        <div className="text-right">
                          <span className="tabular-nums font-medium text-slate-900">
                            {formatMoney(invoice.total_amount)}
                          </span>
                          <span className="ml-2 tabular-nums text-slate-600">
                            (Due: {formatMoney(invoice.balance_due)})
                          </span>
                        </div>
                      </div>
                      {voidable ? (
                        <div className="mt-2.5 flex justify-end">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setVoidingInvoice(invoice);
                            }}
                            className="inline-flex items-center gap-1 rounded px-2.5 py-1 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-300 hover:bg-slate-100"
                          >
                            <Ban size={12} /> Void invoice
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>

      <nav className="mt-4 flex items-center justify-between">
        <Button onClick={() => update({ cursor: "" })} disabled={!cursor || query.isFetching}>
          <ChevronLeft size={16} /> First
        </Button>
        <span className="text-xs text-slate-500">
          {query.isFetching ? "Loading…" : cursor ? "Next page" : "First page"}
        </span>
        <Button
          onClick={() => nextCursor && update({ cursor: nextCursor })}
          disabled={!nextCursor || query.isFetching}
        >
          Next <ChevronRight size={16} />
        </Button>
      </nav>

      {voidingInvoice ? (
        <VoidInvoiceModal
          invoice={voidingInvoice}
          pending={voidMutation.isPending}
          error={voidMutation.error}
          onClose={() => {
            setVoidingInvoice(null);
            voidMutation.reset();
          }}
          onConfirm={(reason) => {
            voidMutation.mutate({ id: voidingInvoice.id, reason });
          }}
        />
      ) : null}
    </>
  );
}

/** True when an invoice can be voided directly by an administrator. */
function canVoidInvoice(invoice: Invoice, role: string | undefined): boolean {
  if (!isAccountAdmin(role)) return false;
  if (invoice.status === "void") return false;
  const paid = Number(invoice.amount_paid?.amount ?? 0);
  const credited = Number(invoice.credited_amount?.amount ?? 0);
  return paid === 0 && credited === 0;
}

/** `next` is an absolute URL; the cursor is its trailing query parameter. */
function cursorFrom(next: string | null | undefined): string | undefined {
  if (!next) return undefined;
  return new URL(next, window.location.origin).searchParams.get("cursor") ?? undefined;
}

/** Copy is chosen by machine code, never by parsing the message. */
function ErrorState({ error }: { error: unknown }) {
  if (!(error instanceof ApiError)) {
    return <EmptyState title="Could not load invoices" body="Please try again." />;
  }
  if (error.isPlanLimit) {
    return (
      <EmptyState
        title="Billing is not on your plan"
        body={`${error.message} Upgrade to add invoicing.`}
      />
    );
  }
  return <EmptyState title="Could not load invoices" body={error.message} />;
}

function VoidInvoiceModal({
  invoice,
  pending,
  error,
  onClose,
  onConfirm,
}: {
  invoice: Invoice;
  pending: boolean;
  error: unknown;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState("Cancelled");
  return (
    <Modal title={`Void invoice ${invoice.number || "Draft"}`} onClose={onClose}>
      <p className="text-sm text-slate-600">
        Voiding updates the invoice status to <strong>Void</strong> and sets its balance due to <strong>0.00</strong>. This action cannot be undone.
      </p>
      <label className="mt-4 block">
        <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
          Reason
        </span>
        <textarea
          rows={2}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Why is this invoice being voided?"
          className="w-full rounded-md border-0 bg-white px-2.5 py-2 text-sm text-slate-900 ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-900"
        />
      </label>
      {error ? (
        <p role="alert" className="mt-3 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error instanceof ApiError ? error.message : "Could not void the invoice."}
        </p>
      ) : null}
      <div className="mt-5 flex justify-end gap-2">
        <Button onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={pending || reason.trim() === ""}
          onClick={() => onConfirm(reason.trim())}
        >
          {pending ? "Voiding…" : "Void invoice"}
        </Button>
      </div>
    </Modal>
  );
}
